import { useMemo } from 'react'

import { getValueOfCssVariable } from '@/lib/utils'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { useLayoutWithMapOrigin } from '@components/storeMap/utils'
import { useGlobalizedFacings } from '@state/layout/queries'
import { type FacingWithGeometry } from '@utils/geometry'

import type { StockLevel } from '@state/product'

function useOOSFacings(stockLevel: StockLevel): FacingWithGeometry[] {
  const { data: layout } = useLayoutWithMapOrigin({ complete: true })
  const { mapOrigin } = useStoreMapContext()
  const { data: facingGeometries } = useGlobalizedFacings({ mapOrigin })

  return useMemo((): FacingWithGeometry[] => {
    if (!layout || !facingGeometries) return []

    return layout.shelves.flatMap(shelf => {
      return shelf.layers.flatMap(layer => {
        return layer.facings.filter(facing => facing.stockLevel === stockLevel)
      })
    })
      .map(facing => facingGeometries.get(facing.id))
      .filter((facing): facing is FacingWithGeometry => !!facing)
  }, [layout, facingGeometries, stockLevel])
}

const oosColor = getValueOfCssVariable('--color-out-of-shelf')
const unknownColor = getValueOfCssVariable('--color-unknown-stock')

interface FacingStateLayerProps {
  stockLevel: StockLevel
}

function FacingStateLayer({ stockLevel }: FacingStateLayerProps) {
  const facings = useOOSFacings(stockLevel)
  const color = stockLevel === 'NO_STOCK' ? oosColor : unknownColor

  return (
    <>
      {facings.map(tf => (
        <mesh
          key={tf.facingId}
          position={[tf.geometry.x, tf.geometry.z, tf.geometry.y]}
          quaternion={tf.quaternion}
        >
          <boxGeometry args={[tf.geometry.width, tf.geometry.height, 0.01]} />
          <meshStandardMaterial color={color} />
        </mesh>
      ))}

    </>
  )
}

export default FacingStateLayer